'use client';

import * as React from 'react';
import { Download, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useImportBundleProgress } from '@/lib/api/funnels';

/**
 * Optional step-config groups a funnel bundle can carry. Mirrors
 * BUNDLE_CONFIG_GROUPS on the server (src/app/services/funnels/bundles/schema.ts).
 */
export const BUNDLE_GROUPS = ['payments', 'shipping', 'address', 'access', 'scripts', 'offers'] as const;
export type BundleGroup = (typeof BUNDLE_GROUPS)[number];
export type BundleIncludes = Record<BundleGroup, boolean>;

/** What a bundle exported before the includes manifest carried (server LEGACY_BUNDLE_INCLUDES). */
const LEGACY_INCLUDES: BundleIncludes = {
  payments: false,
  shipping: true,
  address: true,
  access: true,
  scripts: false,
  offers: true,
};

/** Export defaults: portable step settings on, account-bound ones off. */
const EXPORT_DEFAULTS: BundleIncludes = {
  payments: false,
  shipping: true,
  address: true,
  access: true,
  scripts: false,
  offers: false,
};

/** Loose shape of a parsed bundle file — only what the dialogs display. */
export interface ParsedBundleFile {
  id?: string;
  name?: string;
  includes?: Partial<BundleIncludes>;
  funnel?: { nodes?: unknown[] };
  studio?: { pages?: Record<string, unknown> };
}

export function bundleFileIncludes(bundle: ParsedBundleFile): BundleIncludes {
  return { ...LEGACY_INCLUDES, ...(bundle.includes ?? {}) };
}

interface GroupChecklistProps {
  value: BundleIncludes;
  onChange: (next: BundleIncludes) => void;
  /** Groups that can be toggled; the rest render disabled with a hint. */
  available?: Partial<Record<BundleGroup, boolean>>;
  disabled?: boolean;
}

function GroupChecklist({ value, onChange, available, disabled }: GroupChecklistProps) {
  const { t } = useTranslation('common');
  return (
    <div className="space-y-3">
      {BUNDLE_GROUPS.map((group) => {
        const isAvailable = available ? Boolean(available[group]) : true;
        const id = `bundle-group-${group}`;
        return (
          <div key={group} className="flex items-start gap-3">
            <Checkbox
              id={id}
              checked={isAvailable && value[group]}
              disabled={disabled || !isAvailable}
              onCheckedChange={(checked) => onChange({ ...value, [group]: checked === true })}
              className="mt-0.5"
            />
            <div className="grid gap-0.5 leading-tight">
              <Label htmlFor={id} className={!isAvailable ? 'text-muted-foreground' : undefined}>
                {t(`funnelsTable.bundle.groups.${group}.label`)}
                {!isAvailable && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {t('funnelsTable.bundle.notInFile')}
                  </span>
                )}
              </Label>
              <p className="text-xs text-muted-foreground">{t(`funnelsTable.bundle.groups.${group}.description`)}</p>
            </div>
          </div>
        );
      })}
      <p className="pt-1 text-xs text-muted-foreground">{t('funnelsTable.bundle.alwaysExcluded')}</p>
    </div>
  );
}

interface FunnelBundleExportDialogProps {
  open: boolean;
  funnelName?: string;
  isPending?: boolean;
  onClose: () => void;
  onConfirm: (include: BundleIncludes) => void;
}

export function FunnelBundleExportDialog({ open, funnelName, isPending, onClose, onConfirm }: FunnelBundleExportDialogProps) {
  const { t } = useTranslation('common');
  const [include, setInclude] = React.useState<BundleIncludes>(EXPORT_DEFAULTS);

  React.useEffect(() => {
    if (open) setInclude(EXPORT_DEFAULTS);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !isPending && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('funnelsTable.bundle.export.title')}</DialogTitle>
          <DialogDescription>
            {t('funnelsTable.bundle.export.description', { name: funnelName ?? '' })}
          </DialogDescription>
        </DialogHeader>
        <GroupChecklist value={include} onChange={setInclude} disabled={isPending} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('funnelsTable.bundle.cancel')}
          </Button>
          <Button onClick={() => onConfirm(include)} disabled={isPending}>
            <Download className="mr-2 h-4 w-4" />
            {t('funnelsTable.bundle.export.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ImportProgressBarProps {
  jobId: string | null;
}

/** Bar + step text fed by the server's step events while the import runs. */
function ImportProgressBar({ jobId }: ImportProgressBarProps) {
  const { t } = useTranslation('common');
  const { data } = useImportBundleProgress(jobId);
  const step = data?.step ?? 'starting';
  const percent = data?.percent ?? 0;
  const label =
    step === 'failed'
      ? data?.message || t('funnelsTable.bundle.import.steps.failed')
      : t(`funnelsTable.bundle.import.steps.${step}`, {
          current: data?.current ?? 0,
          total: data?.total ?? 0,
          label: data?.label ?? '',
        });
  return (
    <div className="space-y-2" role="status" aria-live="polite">
      <Progress value={percent} aria-label={t('funnelsTable.bundle.import.progressLabel')} />
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{label}</span>
        <span className="tabular-nums">{percent}%</span>
      </div>
    </div>
  );
}

interface FunnelBundleImportDialogProps {
  bundle: ParsedBundleFile | null;
  isPending?: boolean;
  /** Job id of the running import; drives the progress bar while pending. */
  jobId?: string | null;
  onClose: () => void;
  onConfirm: (include: BundleIncludes) => void;
}

export function FunnelBundleImportDialog({
  bundle,
  isPending,
  jobId = null,
  onClose,
  onConfirm,
}: FunnelBundleImportDialogProps) {
  const { t } = useTranslation('common');
  const carried = React.useMemo(() => (bundle ? bundleFileIncludes(bundle) : LEGACY_INCLUDES), [bundle]);
  const [include, setInclude] = React.useState<BundleIncludes>(carried);

  React.useEffect(() => {
    setInclude(carried);
  }, [carried]);

  const stepCount = bundle?.funnel?.nodes?.length ?? 0;
  const pageCount = bundle?.studio?.pages ? Object.keys(bundle.studio.pages).length : 0;

  return (
    <Dialog open={bundle !== null} onOpenChange={(next) => !next && !isPending && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('funnelsTable.bundle.import.title')}</DialogTitle>
          <DialogDescription>
            {t(isPending ? 'funnelsTable.bundle.import.descriptionPending' : 'funnelsTable.bundle.import.description', {
              name: bundle?.name ?? bundle?.id ?? '',
              steps: stepCount,
              pages: pageCount,
            })}
          </DialogDescription>
        </DialogHeader>
        {isPending ? (
          <ImportProgressBar jobId={jobId} />
        ) : (
          <GroupChecklist value={include} onChange={setInclude} available={carried} disabled={isPending} />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('funnelsTable.bundle.cancel')}
          </Button>
          <Button onClick={() => onConfirm(include)} disabled={isPending}>
            <Upload className="mr-2 h-4 w-4" />
            {isPending ? t('funnelsTable.bundle.import.pending') : t('funnelsTable.bundle.import.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
